import React, {
	useState,
	useEffect,
	useRef,
	useContext,
	useMemo,
	lazy,
	Suspense,
} from "react";
import { Context } from "Context";
import { mapString, removeHTMLTags, mapSuggestedSearchToLinks } from "utils";
import { localizeLinkUrl } from "utils/language";
import { AccessibleText, Button } from "@wdpr/groot-components";
import useFetch from "hooks/useFetch";
import useSearchTracking from "hooks/useSearchTracking";
import appConfig from "config.json";
import { getSvgBackground } from "utils/svg";
const SuggestionList = lazy(() => import("./SuggestionList"));

const { searchService, searchHistory, brands } = appConfig;
const { origin, pathname } = window?.location;
const searchHistoryTimeout = 3000;
const mainClass = "syndicated-navigation__chrome__search";
const minSearchQueryLength = 3;
let cleaned = false;
let timeoutId = null;

const isSearchQueryValid = (searchQuery = "", allowedChars = 2) => {
	const trimmedSearchLength = searchQuery?.replace(/ +/g, "").length;
	return trimmedSearchLength && trimmedSearchLength >= allowedChars;
};

const generateSearchQuery = (query = "") => {
	return query?.trim().replace("...", "") || "";
};

const SearchBar = ({
	handleSearchBarToggle,
	searchAutocomplete,
	isClientReady,
	isAutocompleteEnabled,
	suggestedSearch,
}) => {
	const {
		headerData: { chrome },
		config: { basePath, searchBar, brand, locale, absoluteLinksFromBrand },
		isMobileView,
		profileData: { userType },
		cdnAssetsUrl,
	} = useContext(Context);

	const { saveAutocompleteSelection, saveQuerySearch, siteSearchStorage } =
		useSearchTracking(searchAutocomplete);

	const minSearchLength =
		brands[brand]?.minSearchLength?.[locale] || minSearchQueryLength;
	const defaultSearchService = !brands[brand]?.searchService;
	const searchUrlParams = new URLSearchParams(window?.location.search);
	const improvedSiteSearch =
		(brands[brand]?.improvedSiteSearch && chrome?.maxResults) || false;
	const initialmaxResults = improvedSiteSearch ? chrome?.maxResults || 8 : 10;
	const [cursor, setCursor] = useState(-1);
	const [searchQuery, setSearchQuery] = useState(
		searchUrlParams.get("searchQuery") || ""
	);
	const [searchParams, setSearchParams] = useState(null);
	const [fetchTrigger, setFetchTrigger] = useState(0);
	const [searchHistoryParams, setSearchHistoryParams] = useState(null);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [showSuggestedSearch, setShowSuggestedSearch] = useState(true);
	const [filteredQuery, setFilteredQuery] = useState("");

	const accessibilityTextSearchResults =
		chrome?.accessibilityTextSearchResults || "Search dialog {total} results";

	const { response, error } = useFetch(
		searchParams
			? defaultSearchService
				? `${searchService.apiUrl}?${searchParams}`
				: `${basePath}${brands[brand].searchService.apiUrl}`
			: "",
		searchParams
			? defaultSearchService
				? { method: "GET" }
				: searchParams
			: null,
		0,
		fetchTrigger
	);
	const ref = useRef(null);
	const inputRef = useRef(null);
	const memoizedLinks = useMemo(() => response?.links || [], [response]);
	const suggestedLinks = useMemo(
		() => mapSuggestedSearchToLinks(suggestedSearch),
		[suggestedSearch]
	);
	const shouldShowSuggestedSearch = useMemo(
		() =>
			isMobileView &&
			isAutocompleteEnabled &&
			showSuggestedSearch &&
			!searchQuery &&
			suggestedLinks.length > 0,
		[isMobileView, isAutocompleteEnabled, searchQuery, showSuggestedSearch, suggestedLinks.length]
	);
	const { response: searchHistoryResponse, error: searchHistoryError } =
		useFetch(
			searchHistoryParams ? searchHistory.apiUrl : "",
			searchHistoryParams,
			0,
			searchHistoryParams
		);

	useEffect(() => {
		if (isMobileView) {
			inputRef.current?.focus();
		}
	}, [isMobileView]);

	const handleInnerClick = () => {
		if (searchQuery && response?.links?.length > 0) {
			setShowSuggestions(true);
		}
	};

	useEffect(() => {
		if (searchHistoryParams) {
			navigateToSearchResult(cursor);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchHistoryResponse, searchHistoryError]);

	useEffect(() => {
		if (cursor !== -1) {
			return;
		}
		setShowSuggestions(false);
		if (siteSearchStorage && isAutocompleteEnabled && !isClientReady) return;
		debouncedLoadResult();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery, isClientReady]);

	useEffect(() => {
		if (
			isClientReady &&
			searchAutocomplete &&
			siteSearchStorage &&
			searchUrlParams.get("searchQuery")
		) {
			loadResults(searchAutocomplete);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchAutocomplete, isClientReady]);

	const loadResults = (isAutocomplete = searchAutocomplete) => {
		const validSearchQuery = searchQuery.trim().replace(/  +/g, " ");
		if (isSearchQueryValid(searchQuery, minSearchLength)) {
			const pageName =
				pathname
					.split("/")
					.filter((part) => part.length)
					.join("_") || "homepage";
			if (defaultSearchService) {
				setSearchParams(
					mapString(
						searchService.baseParams,
						encodeURIComponent(validSearchQuery),
						pageName,
						"",
						improvedSiteSearch,
						initialmaxResults
					) + (isAutocomplete ? "&autocomplete=true" : "")
				);
			} else {
				setSearchParams({
					method: "POST",
					body: mapString(
						brands[brand].searchService.baseParams,
						encodeURIComponent(validSearchQuery),
						pageName,
						userType || "GUEST",
						improvedSiteSearch,
						initialmaxResults
					),
					headers: {
						"Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
					},
				});
			}
			setFetchTrigger((fetch) => fetch + 1);
			setCursor(-1);
		} else if (validSearchQuery.length === 0) {
			setShowSuggestions(false);
			setShowSuggestedSearch(true);
		}
	};

	const debounce = (func, delay) => {
		return (...args) => {
			if (timeoutId) {
				// Clear the previous timeout
				clearTimeout(timeoutId);
			}
			// Set a new timeout
			timeoutId = setTimeout(() => {
				func(...args);
			}, delay);
		};
	};

	const debouncedLoadResult = debounce(loadResults, 300);

	useEffect(() => {
		if (
			response &&
			response.links?.length > 0 &&
			searchQuery &&
			document?.activeElement === ref.current.querySelector("input")
		) {
			setShowSuggestions(true);
		} else if (!response || response.links?.length === 0) {
			setShowSuggestions(false);
		}
		setFilteredQuery(
			generateSearchQuery(response?.filteredQuery || searchQuery)
		);
		if (
			!cleaned &&
			searchQuery &&
			searchUrlParams.get("searchQuery") === searchQuery &&
			response &&
			!response.filteredQuery
		) {
			setSearchQuery("");
			setFilteredQuery("");
			cleaned = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [response]);

	useEffect(() => {
		setFilteredQuery(searchQuery);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!ref.current?.contains(event.target)) {
				setShowSuggestions(false);
				setShowSuggestedSearch(false);
			}
		};
		document?.addEventListener("click", handleClickOutside, true);
		return () => window?.removeEventListener("click", handleClickOutside, true);
	}, []);

	if (!chrome?.searchDisney) {
		return null;
	}

	const makeSearchHistoryRequest = (position, event) => {
		event?.preventDefault();
		setCursor(position);
		setSearchQuery(response?.links?.[position]?.title);
		saveAutocompleteSelection(response);
		setSearchHistoryParams({
			method: "PUT",
			body: JSON.stringify({
				title: response?.links?.[position]?.fullTitle,
				position,
				documentId: response?.links?.[position]?.id,
				searchId: response?.searchId,
				improved: improvedSiteSearch,
				initialmaxResults,
			}),
			headers: {
				"Content-Type": "application/json",
			},
		});
		setTimeout(() => {
			navigateToSearchResult(position);
		}, searchHistoryTimeout);
		return;
	};

	const handleSuggestedLinkClick = (position, event) => {
		event?.preventDefault();
		setCursor(position);
		setSearchQuery(suggestedLinks?.[position]?.title || "");
		const link = suggestedLinks[position];
		if (link?.url) window?.location.assign(link.url);
	};

	const search = ({ isManualQuery } = {}) => {
		if (isSearchQueryValid(searchQuery, minSearchLength)) {
			if (isManualQuery && response) {
				saveQuerySearch(response);
			}
			window?.location.assign(
				mapString(
					chrome?.searchPage ||
						`${
							absoluteLinksFromBrand ? "" : origin
						}${basePath}/search/?searchQuery={query}`,
					encodeURIComponent(searchQuery)
				)
			);
		}
	};

	const navigateToSearchResult = (cursorPosition) => {
		if (cursorPosition === -1) {
			return;
		}
		const searchUrl = response?.links?.[cursorPosition]?.url;
		if (!searchUrl || searchUrl === "" || searchUrl === "/#") {
			search();
		} else {
			window?.location.assign(localizeLinkUrl(searchUrl));
		}
	};

	const keyboardNavigation = (e) => {
		const isSuggested = shouldShowSuggestedSearch;
		const activeLinks = isSuggested ? suggestedLinks : response?.links || [];
		let currentCursor = cursor;

		if (e.key === "ArrowDown" || e.key === "ArrowUp") {
			currentCursor = cursor > 0 ? cursor - 1 : 0;
			if (e.key === "ArrowDown") {
				currentCursor = cursor < activeLinks.length - 1 ? cursor + 1 : cursor;
			}
			setCursor(currentCursor);
			if (!isSuggested) {
				setSearchQuery(response?.links?.[currentCursor]?.title);
			}
		}

		if (e.key === "Enter") {
			if (isSuggested && currentCursor >= 0) {
				const link = suggestedLinks[currentCursor];
				if (link?.url) window?.location.assign(link.url);
			} else if (currentCursor === -1) {
				search({ isManualQuery: true });
			} else if (currentCursor >= 0) {
				makeSearchHistoryRequest(currentCursor);
			}
		}
		if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter") {
			setCursor(-1);
		}

		if (e.key === "Escape") {
			setShowSuggestions(false);
			setShowSuggestedSearch(false);
		}
	};

	return searchBar ? (
		<div
			role="search"
			ref={ref}
			className={`${mainClass} ${
				searchAutocomplete ? `${mainClass}--autocomplete ` : ""
			}${mainClass}--${brand}`}
		>
			<div
				className={
					`${mainClass}__form ${mainClass}__form--${brand} ${mainClass}__form${
						searchAutocomplete ? "--autocomplete" : ""
					} ` + (showSuggestions ? ` ${mainClass}__form--focus` : "")
				}
			>
				{(searchAutocomplete || !searchAutocomplete) && !isMobileView && (
					<Button
						accessibleText={chrome?.confirmSearch}
						className={`${mainClass}__form__button syndicated-icon syndicated-icon--search${
							filteredQuery ? "" : " syndicated-icon--search--disabled"
						}`}
						customStyles
						onClick={() => search({ isManualQuery: true })}
						style={getSvgBackground("search")}
					/>
				)}
				{searchAutocomplete && isMobileView && (
					<Button
						accessibleText={chrome?.confirmSearch}
						className={`${mainClass}__form__button syndicated-icon syndicated-icon--search${
							filteredQuery ? "" : " syndicated-icon--search--disabled"
						}`}
						customStyles
						onClick={handleSearchBarToggle}
						style={getSvgBackground("leading")}
					/>
				)}

				<input
					ref={inputRef}
					aria-label={chrome?.searchDisney}
					onClick={handleInnerClick}
					className={`${mainClass}__form__input ${mainClass}__form__input--${brand}${
						searchQuery ? "" : ` ${mainClass}__form__input--empty`
					}`}
					type="search"
					value={searchQuery}
					onChange={(event) => {
						setSearchQuery(removeHTMLTags(event.target.value));
						setFilteredQuery("");
						setShowSuggestedSearch(false);
					}}
					placeholder={`${chrome?.searchDisney}`}
					maxLength="254"
					autoComplete="off"
					name="searchQuery"
					onKeyDown={(e) => keyboardNavigation(e)}
					role="combobox"
					aria-expanded={showSuggestions ? "true" : "false"}
					aria-controls={`${mainClass}__autosuggest`}
					aria-autocomplete="list"
				/>
				{searchQuery && (
					<Button
						accessibleText={chrome?.clearSearch}
						className={`${mainClass}__form__button syndicated-icon syndicated-icon--close-button`}
						customStyles
						onClick={() => setSearchQuery("")}
						style={{
							backgroundImage: `url(${cdnAssetsUrl}/icons/pep/close-button.svg)`,
						}}
					/>
				)}
			</div>
			<AccessibleText ariaLive="assertive">
				{searchQuery &&
					showSuggestions &&
					mapString(
						accessibilityTextSearchResults,
						response?.links?.length || 0
					)}
			</AccessibleText>
			{shouldShowSuggestedSearch && (
				<Suspense fallback={null}>
					<span className={`${mainClass}__suggested-text--non-txp`}>
						{suggestedSearch?.title}
					</span>
					<SuggestionList
						links={suggestedLinks}
						cursor={cursor}
						searchAutocomplete
						isSuggestedSearch
						makeSearchHistoryRequest={handleSuggestedLinkClick}
						filteredQuery=""
						search={() => {}}
						showSuggestions={true}
					/>
				</Suspense>
			)}
			{showSuggestions && searchQuery && (
				<Suspense fallback={null}>
					<SuggestionList
						links={memoizedLinks}
						cursor={cursor}
						improvedSiteSearch={improvedSiteSearch}
						searchAutocomplete={searchAutocomplete}
						makeSearchHistoryRequest={makeSearchHistoryRequest}
						filteredQuery={filteredQuery}
						search={search}
						showSuggestions={showSuggestions}
					/>
				</Suspense>
			)}
		</div>
	) : null;
};
export default SearchBar;
